import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import { Progress as PxProgress } from '@roi-pros/px-react';
import toast from 'react-hot-toast';

interface IncludedCredits {
  monthly: number;
  used: number;
  remaining: number;
  resetDate: string | null;
  status: 'active' | 'pending';
}

interface TrialCredits {
  remaining: number;
  endsAt: string | null;
}

interface CreditBalance {
  available: number;
  included: IncludedCredits;
  purchased: {
    remaining: number;
  };
  trial?: TrialCredits;
}

interface CreditSource {
  plan: {
    code: string;
    name: string;
    baseCredits: number;
  };
  outcomes: Array<{
    code: string;
    displayName: string;
    includedCredits: number;
  }>;
}

interface CreditPack {
  packCode: string;
  credits: number;
  amountCents: number;
  displayName: string;
}

interface CreditsTabProps {
  workspaceId: string;
  authToken: string;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Credits settings tab
 *
 * Features:
 * - Available balance
 * - Included/purchased/trial credits
 * - Reset date
 * - Credit sources breakdown
 * - Available packs for purchase
 */
export function CreditsTab({ workspaceId, authToken }: CreditsTabProps) {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [sources, setSources] = useState<CreditSource | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [purchasingPack, setPurchasingPack] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  async function loadData() {
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/credits`, authToken, {
        
      });

      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance || null);
        setSources(data.sources || null);
        setPacks(data.packs || []);
      } else {
        toast.error('Failed to load credits');
      }
    } catch {
      toast.error('Failed to load credits');
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePurchasePack(packCode: string) {
    setPurchasingPack(packCode);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/credits/purchase`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          packCode,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.success('Credits purchased successfully');
          loadData();
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to purchase credits');
      }
    } catch {
      toast.error('Failed to purchase credits');
    } finally {
      setPurchasingPack(null);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  // B7 adoption (review finding): the usage fraction passed to the Platform Progress must
  // always be a finite 0..1 value, so the determinate progressbar never advertises an
  // invalid aria-valuenow. This derivation stays application-owned:
  //   monthly > 0        -> used / monthly, capped at 1 (same fraction the prior markup used)
  //   monthly <= 0       -> 0 (e.g. zero-included-credit / free plans; avoids 0/0 = NaN)
  //   non-finite results -> 0
  const rawUsageRatio =
    balance && balance.included.monthly > 0
      ? balance.included.used / balance.included.monthly
      : 0;
  const usageProgress = Number.isFinite(rawUsageRatio) ? Math.min(1, rawUsageRatio) : 0;

  return (
    <div className="space-y-6">
      {/* Available Balance */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-sm p-6 text-white">
        <h2 className="text-sm font-medium text-blue-100">Available Credits</h2>
        <p className="text-4xl font-bold mt-2">{balance?.available ?? 0}</p>
        {balance?.included.resetDate && balance.included.status === 'active' && (
          <p className="text-sm text-blue-200 mt-2">
            Resets {formatDate(balance.included.resetDate)}
          </p>
        )}
      </div>

      {/* Credit Breakdown */}
      {balance && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Credit Breakdown</h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Included Credits */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Monthly Included</p>
                <p className="text-sm text-gray-500">
                  {balance.included.remaining} remaining of {balance.included.monthly}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{balance.included.remaining}</p>
                {balance.included.status === 'pending' && (
                  <span className="text-xs text-amber-600 font-medium">Pending</span>
                )}
              </div>
            </div>

            {/* Usage Progress */}
            <div className="px-4">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                <span>Used this period</span>
                <span>{balance.included.used} / {balance.included.monthly}</span>
              </div>
              {/* B7 Platform adoption: the usage bar now renders via the Platform Experience
                  Progress component (@roi-pros/px-react). The application owns the derived
                  `usageProgress` value (see above); the Platform owns HOW the bar renders
                  (track/fill tokens, determinate progressbar a11y). Presentation only —
                  no call sites changed. */}
              <PxProgress api={{ value: usageProgress }} />
            </div>

            {/* Purchased Credits */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Purchased Credits</p>
                <p className="text-sm text-gray-500">Never expire</p>
              </div>
              <p className="text-2xl font-bold text-green-600">{balance.purchased.remaining}</p>
            </div>

            {/* Trial Credits */}
            {balance.trial && balance.trial.remaining > 0 && (
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Trial Credits</p>
                  {balance.trial.endsAt && (
                    <p className="text-sm text-gray-500">
                      Expires {formatDate(balance.trial.endsAt)}
                    </p>
                  )}
                </div>
                <p className="text-2xl font-bold text-amber-600">{balance.trial.remaining}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credit Sources */}
      {sources && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Credit Sources</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {/* Plan Credits */}
            <div className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{sources.plan.name} Plan</p>
                <p className="text-sm text-gray-500">Base plan credits</p>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                +{sources.plan.baseCredits}
              </p>
            </div>

            {/* Outcome Credits */}
            {sources.outcomes.filter((o) => o.includedCredits > 0).map((outcome) => (
              <div key={outcome.code} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{outcome.displayName}</p>
                  <p className="text-sm text-gray-500">Outcome bonus credits</p>
                </div>
                <p className="text-lg font-semibold text-blue-600">
                  +{outcome.includedCredits}
                </p>
              </div>
            ))}

            {sources.outcomes.filter((o) => o.includedCredits > 0).length === 0 && (
              <div className="px-6 py-4 text-center text-gray-500 text-sm">
                Add outcomes to earn bonus credits each month
              </div>
            )}
          </div>
        </div>
      )}

      {/* Purchase Credits */}
      {packs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Purchase Credits</h2>
            <p className="text-sm text-gray-500 mt-1">
              Purchased credits never expire and are used after monthly credits
            </p>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {packs.map((pack) => {
              const pricePerCredit = (pack.amountCents / pack.credits).toFixed(1);
              const isPopular = pack.packCode === 'pack-1200';

              return (
                <div
                  key={pack.packCode}
                  className={`relative rounded-lg border-2 p-4 ${
                    isPopular ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  {isPopular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded">
                      Most Popular
                    </span>
                  )}
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{pack.credits}</p>
                    <p className="text-sm text-gray-500">credits</p>
                    <p className="text-xl font-semibold text-gray-900 mt-3">
                      {formatCents(pack.amountCents)}
                    </p>
                    <p className="text-xs text-gray-500">{pricePerCredit}¢ per credit</p>
                    <button
                      onClick={() => handlePurchasePack(pack.packCode)}
                      disabled={purchasingPack === pack.packCode}
                      className={`w-full mt-4 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
                        isPopular
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {purchasingPack === pack.packCode ? 'Processing...' : 'Purchase'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
